import React, { useEffect, useRef, useMemo } from 'react';
import {
  defineAdSlot,
  displayAdSlot,
  destroyAdSlot,
  startAdRefresh,
  onAdSlotRenderEnded,
} from './gpt';
import type { GoogleAdsPlacement } from './types';

export interface AdSlotProps {
  /**
   * Unique ID for the ad container div
   */
  divId: string;

  /**
   * Google Ads placement configuration
   */
  adPlacement: GoogleAdsPlacement;

  /**
   * Optional CSS class for the container
   */
  className?: string;

  /**
   * Placement name for logging/debugging
   */
  placement?: string;

  /**
   * Optional: Enable automatic ad refresh
   * DraftKings spec recommends 30-60 second intervals
   */
  enableRefresh?: boolean;

  /**
   * Optional: Refresh interval in seconds (default: 45)
   */
  refreshIntervalSeconds?: number;

  /**
   * Optional: Maximum number of refreshes (0 = unlimited)
   */
  maxRefreshes?: number;

  /**
   * Reserved container min-height in px. Defaults to the tallest configured ad size.
   * Pass `null` to omit inline min-height (e.g. when `className` supplies responsive min-heights).
   */
  reserveMinHeight?: number | null;

  /**
   * Called once when the slot resolves with no creative — either GAM reported an
   * empty render or nothing arrived within `emptyTimeoutMs`. Lets the parent
   * collapse the space it reserved for the ad.
   */
  onEmpty?: () => void;

  /**
   * Grace period in ms to wait for a creative before reporting the slot empty.
   * Long enough for gpt.js to load and the ad request to round-trip.
   */
  emptyTimeoutMs?: number;
}

/** Default grace period before an unfilled slot is reported empty. */
const DEFAULT_EMPTY_TIMEOUT_MS = 2500;

/**
 * AdSlot component for rendering Google Ad Manager ads
 * Based on DraftKings GPT integration requirements
 * Supports responsive size mappings and automatic ad refresh
 */
export const AdSlot: React.FC<AdSlotProps> = ({
  divId,
  adPlacement,
  className = '',
  placement = 'unknown',
  enableRefresh = false,
  refreshIntervalSeconds = 45,
  maxRefreshes = 0,
  reserveMinHeight,
  onEmpty,
  emptyTimeoutMs = DEFAULT_EMPTY_TIMEOUT_MS,
}) => {
  // Extract values from adPlacement
  const adUnitPath = useMemo(() => adPlacement.adUnitPath || '', [adPlacement.adUnitPath]);
  const sizes = useMemo(() => adPlacement.sizes || [], [adPlacement.sizes]);
  const sizeMappings = useMemo(() => adPlacement.sizeMappings, [adPlacement.sizeMappings]);
  const targeting = useMemo(() => adPlacement.targeting || undefined, [adPlacement.targeting]);
  const adRef = useRef<HTMLDivElement>(null);
  const isDefinedRef = useRef(false);
  const stopRefreshRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Define the ad slot only once
    if (!isDefinedRef.current) {
      defineAdSlot({
        adUnitPath,
        sizes,
        divId,
        targeting,
        sizeMappings: sizeMappings ?? undefined,
      });

      isDefinedRef.current = true;
    }

    // Display the ad
    displayAdSlot(divId);

    // Start automatic refresh if enabled
    if (enableRefresh) {
      stopRefreshRef.current = startAdRefresh({
        divIds: [divId],
        intervalSeconds: refreshIntervalSeconds,
        maxRefreshes,
      });
    }

    // Cleanup: destroy slot and stop refresh when component unmounts
    return () => {
      // Stop ad refresh if it was started
      if (stopRefreshRef.current) {
        stopRefreshRef.current();
        stopRefreshRef.current = null;
      }

      if (isDefinedRef.current) {
        destroyAdSlot(divId);
        isDefinedRef.current = false;
      }
    };
  }, [
    divId,
    adUnitPath,
    sizes,
    sizeMappings,
    targeting,
    placement,
    enableRefresh,
    refreshIntervalSeconds,
    maxRefreshes,
  ]);

  // Report an unfilled slot so the parent can collapse the reserved space. The
  // timeout also covers the cases GPT never reports on at all: gpt.js blocked by
  // an ad blocker, or the request never completing.
  useEffect(() => {
    if (!onEmpty) return;

    let settled = false;
    const reportEmpty = () => {
      if (settled) return;
      settled = true;
      onEmpty();
    };

    const timer = setTimeout(reportEmpty, emptyTimeoutMs);
    const unsubscribe = onAdSlotRenderEnded(divId, isEmpty => {
      if (isEmpty) {
        reportEmpty();
        return;
      }
      // A creative rendered — never collapse this slot.
      settled = true;
      clearTimeout(timer);
    });

    return () => {
      clearTimeout(timer);
      unsubscribe();
    };
  }, [divId, onEmpty, emptyTimeoutMs]);

  const containerMinHeight =
    reserveMinHeight === null
      ? undefined
      : reserveMinHeight ?? Math.max(...sizes.map(s => s[1]));

  return (
    <div
      data-test-id={`ad-slot-${placement}`}
      className={`ad-slot-container relative w-full flex items-center justify-center ${className}`}
      style={containerMinHeight != null ? { minHeight: `${containerMinHeight}px` } : undefined}
    >
      <div
        id={divId}
        ref={adRef}
        className="ad-slot mx-auto"
        style={{
          maxWidth: `${Math.max(...sizes.map(s => s[0]))}px`,
        }}
      />
    </div>
  );
};

export default AdSlot;
